import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { paymentsApi } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

export default function PaymentCompletePage() {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'verifying' | 'success' | 'failed'>('verifying');

  const orderId = searchParams.get('orderId') || searchParams.get('merchantRequestId');
  const transactionId = searchParams.get('transactionId') || searchParams.get('paymentId');

  const verifyPayment = useMutation({
    mutationFn: () => paymentsApi.verify(orderId!, transactionId || undefined),
    onSuccess: (data) => {
      if (data.status === 'paid') {
        setStatus('success');
      } else {
        setStatus('failed');
      }
    },
    onError: () => {
      setStatus('failed');
    },
  });

  useEffect(() => {
    if (orderId) {
      // Wait 2 seconds before verifying to allow webhook to process
      setTimeout(() => {
        verifyPayment.mutate();
      }, 2000);
    } else {
      setStatus('failed');
    }
  }, [orderId]);

  if (status === 'verifying') {
    return (
      <div className="soleil min-h-screen flex items-center justify-center" style={{ background: "var(--soleil-cream)" }}>
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <Loader2 className="h-16 w-16 animate-spin text-primary mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Verifying Payment</h2>
            <p className="text-muted-foreground">
              Please wait while we confirm your payment...
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="soleil min-h-screen flex items-center justify-center" style={{ background: "var(--soleil-cream)" }}>
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2">{isRTL ? 'تم الدفع بنجاح!' : 'Payment Successful!'}</h2>
            <p className="text-muted-foreground mb-6">
              {isRTL ? 'تم تأكيد تسجيلك. يمكنك الآن الوصول إلى محتوى الدورة.' : 'Your enrollment has been confirmed. You can now access the course content.'}
            </p>
            <div className="space-y-3">
              <Button
                className="w-full"
                onClick={() => navigate('/dashboard')}
              >
                {isRTL ? 'الذهاب لدوراتي' : 'Go to My Courses'}
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate('/courses')}
              >
                {isRTL ? 'تصفح المزيد من الدورات' : 'Browse More Courses'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="soleil min-h-screen flex items-center justify-center" style={{ background: "var(--soleil-cream)" }}>
      <Card className="w-full max-w-md">
        <CardContent className="p-8 text-center">
          <div className="bg-red-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <XCircle className="h-10 w-10 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold mb-2">{isRTL ? 'فشل الدفع' : 'Payment Failed'}</h2>
          <p className="text-muted-foreground mb-6">
            {isRTL ? 'لم نتمكن من معالجة الدفع. يرجى المحاولة مرة أخرى أو التواصل مع الدعم.' : "We couldn't process your payment. Please try again or contact support if the problem persists."}
          </p>
          <div className="space-y-3">
            <Button
              className="w-full"
              onClick={() => navigate('/courses')}
            >
              Back to Courses
            </Button>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => navigate('/contact')}
            >
              Contact Support
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
